import { Close, SendRounded } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, MenuItem, Select, Stack, Typography, useTheme } from '@mui/material'
import { memo, useState } from 'react'
import ApiClient from '../../../backend/ApiClient'
import { IApprovalRequest } from '../../../definitions/Approval'
import { ISettlementSchema } from '../../../definitions/SettlementSchema'
import Formatter from '../../../helpers/Formatter'
import ApprovalManager from '../../../managers/ApprovalManager'
import { useAppDispatch } from '../../../store/Hooks'
import { changeRoute } from '../../../store/slices/SessionSlice'
import useCommand from '../../hooks/useCommand'
import useInvalidate from '../../hooks/useInvalidate'
import useQuery, { buildQueryKey } from '../../hooks/useQuery'
import { snackbar } from '../../services/SnackbarService'
import { IUserRole } from '../../../definitions/Permissions'

interface P {
    approval: IApprovalRequest
    settlement: ISettlementSchema
    open: boolean
    setOpen: (open: boolean) => void
}

const SettlementSubmitModal = ({ approval, settlement, open, setOpen }: P) => {
    const dispatch = useAppDispatch()
    const theme = useTheme()
    const [selectedGroupUUID, setSelectedGroupUUID] = useState<IUserRole['uuid']>()
    const invalidateApproval = useInvalidate({ queryKey: buildQueryKey(`get-approval-${settlement.uuid}`) })
    const invalidateSettlementDetails = useInvalidate({ queryKey: buildQueryKey('settlement-details', settlement.uuid) })
    const invalidateSettlements = useInvalidate({ queryKey: buildQueryKey('settlements' + '/*') })
    const invalidateSettlementsReport = useInvalidate({ queryKey: buildQueryKey('compiledReports-/*') })

    const { response: groups } = useQuery({
        queryKey: buildQueryKey('get-approvals-groups'),
        queryFn: () => ApiClient.getApprovalGroups(['settlements'])
    })

    const { execute: executeSendApprovalToReview, status: statusExecuteSendApprovalToReview } = useCommand({
        commandFn: () => ApiClient.sendSettlementApprovalToReview(settlement.uuid, selectedGroupUUID),
        onFinish: result => {
            if (result.status === 'success') {
                invalidateApproval()
                invalidateSettlementDetails()
                invalidateSettlements()
                invalidateSettlementsReport()
                snackbar.show({ severity: 'success', text: 'Settlement approval sent to review' })
            }
            if (result.status === 'error') {
                snackbar.show({ severity: 'error', text: 'Failed to send settlement approval to review.' })
            }
            setOpen(false)
        }
    })

    const { execute: executeBackToDraft, status: statusExecuteBackToDraft } = useCommand({
        commandFn: (approvalReq: IApprovalRequest) => ApiClient.revertApprovalToDraft(approvalReq.uuid),
        onFinish: result => {
            if (result.status === 'success') {
                invalidateApproval()
                invalidateSettlementDetails()
                invalidateSettlements()
                invalidateSettlementsReport()
                snackbar.show({ severity: 'success', text: 'Settlement approval sent back to draft' })
            }
            if (result.status === 'error') {
                snackbar.show({ severity: 'error', text: 'Failed to sent settlement approval back to draft' })
            }
            setOpen(false)
        }
    })

    const renderModalContent = () => {
        if (!approval) {
            return (
                <>
                    <DialogTitle sx={{ pb: 1 }}>
                        <Box display='flex' justifyContent='space-between' alignItems='center'>
                            <Typography variant='h6'>Send settlement for review</Typography>
                            <IconButton onClick={() => setOpen(false)} size='small'>
                                <Close />
                            </IconButton>
                        </Box>
                    </DialogTitle>
                    <DialogContent sx={{ pb: 2 }}>
                        <Stack spacing={2} mt={3}>
                            <Box>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Stack spacing={2}>
                                            {ApprovalManager.statusField('draft')}
                                            {ApprovalManager.infoField('Entity Name', `Settlement version ${settlement.versioning.version}`)}
                                        </Stack>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Stack spacing={2}>
                                            {ApprovalManager.infoField('Drafted By', 'N/A')}
                                            {ApprovalManager.infoField('Drafted At', Formatter.date(new Date(settlement.runAt), 'mm/dd/yyyy hh:mm:ss'))}
                                        </Stack>
                                    </Grid>
                                </Grid>
                            </Box>
                            <Typography variant='body1'>
                                Change the status to
                                {' '}
                                <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 'bold' }}>
                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'info.main', display: 'inline-block' }} />
                                    Ready for review
                                </Box>
                                {' '}
                                and submit it for approval?
                            </Typography>
                            <Typography variant='subtitle2' color='text.secondary'>
                                Select Approval Group:
                            </Typography>
                            <Select
                                fullWidth
                                required
                                size='small'
                                value={selectedGroupUUID ?? ''}
                                onChange={e => setSelectedGroupUUID(e.target.value)}>
                                {groups?.map((group: IUserRole) => (
                                    <MenuItem key={group?.uuid} value={group?.uuid}>
                                        {group?.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </Stack>
                    </DialogContent>
                    <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                        <LoadingButton
                            startIcon={<SendRounded />}
                            variant='contained'
                            loading={statusExecuteSendApprovalToReview === 'loading'}
                            onClick={() => executeSendApprovalToReview(null)}
                            loadingPosition='start'
                            sx={{ textTransform: 'none' }}
                            disabled={false}>
                            Send For Review
                        </LoadingButton>
                    </DialogActions>
                </>
            )
        }

        const lastChange = approval.changes?.length ? approval.changes[approval.changes.length - 1] : null

        switch (approval.status) {
            case 'draft':
                return (
                    <>
                        <DialogTitle sx={{ pb: 1 }}>
                            <Box display='flex' justifyContent='space-between' alignItems='center'>
                                <Typography variant='h6'>Send {approval?.entityType} for review</Typography>
                                <IconButton onClick={() => setOpen(false)} size='small'>
                                    <Close />
                                </IconButton>
                            </Box>
                        </DialogTitle>
                        <DialogContent sx={{ pb: 2 }}>
                            <Stack spacing={2} mt={3}>
                                <Box>
                                    <Grid container spacing={2}>
                                        <Grid item xs={6}>
                                            <Stack spacing={2}>
                                                {ApprovalManager.statusField(approval.status)}
                                                {ApprovalManager.infoField('Entity Name', approval.entityName)}
                                            </Stack>
                                        </Grid>
                                        <Grid item xs={6}>
                                            <Stack spacing={2}>
                                                {ApprovalManager.infoField('Drafted By', lastChange.userName ?? 'N/A')}
                                                {ApprovalManager.infoField('Drafted At', Formatter.date(new Date(lastChange.changedAt), 'mm/dd/yyyy hh:mm:ss'))}
                                            </Stack>
                                        </Grid>
                                    </Grid>
                                </Box>
                                <Typography variant='body1'>
                                    Change the status to
                                    {' '}
                                    <Box component='span' sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontWeight: 'bold' }}>
                                        <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: 'info.main', display: 'inline-block' }} />
                                        Ready for review
                                    </Box>
                                    {' '}
                                    and submit it for approval?
                                </Typography>
                                <Typography variant='subtitle2' color='text.secondary'>
                                    Select Approval Group:
                                </Typography>
                                <Select
                                    fullWidth
                                    required
                                    size='small'
                                    value={selectedGroupUUID ?? ''}
                                    onChange={e => setSelectedGroupUUID(e.target.value)}>
                                    {groups?.map((group: IUserRole) => (
                                        <MenuItem key={group?.uuid} value={group?.uuid}>
                                            {group?.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Stack>
                        </DialogContent>
                        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                            <LoadingButton
                                startIcon={<SendRounded />}
                                variant='contained'
                                loading={statusExecuteSendApprovalToReview === 'loading'}
                                onClick={() => executeSendApprovalToReview(null)}
                                loadingPosition='start'
                                sx={{ textTransform: 'none' }}
                                disabled={false}>
                                Send For Review
                            </LoadingButton>
                        </DialogActions>
                    </>
                )

            case 'ready-for-review':
                return (
                    <>
                        <DialogTitle sx={{ pb: 1 }}>
                            <Box display='flex' justifyContent='space-between' alignItems='center'>
                                <Typography variant='h6'>Waiting for approval</Typography>
                                <IconButton onClick={() => setOpen(false)} size='small'>
                                    <Close />
                                </IconButton>
                            </Box>
                        </DialogTitle>
                        <DialogContent sx={{ pb: 2 }}>
                            <Stack spacing={2} mt={2}>
                                {ApprovalManager.statusField(approval.status)}
                                {ApprovalManager.infoField('Flow Name', approval.entityName)}
                                {ApprovalManager.infoField('Requested By', lastChange.userName ?? 'N/A')}
                                {ApprovalManager.infoField('Requested At', Formatter.date(new Date(lastChange.changedAt), 'mm/dd/yyyy hh:mm:ss'))}
                            </Stack>
                        </DialogContent>

                        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                            <LoadingButton
                                startIcon={<SendRounded />}
                                variant='contained'
                                loading={statusExecuteBackToDraft === 'loading'}
                                onClick={() => executeBackToDraft(approval)}
                                loadingPosition='start'
                                sx={{ textTransform: 'none' }}
                                disabled={false}>
                                Back To Draft
                            </LoadingButton>
                            <Button
                                variant='contained'
                                color='success'
                                onClick={() => dispatch(changeRoute({ route: 'approvals' }))}
                                sx={{ textTransform: 'none' }}
                                disabled={statusExecuteBackToDraft === 'loading'}>
                                Approval Hub
                            </Button>
                        </DialogActions>
                    </>
                )

            case 'rejected':
                return (
                    <>
                        <DialogTitle sx={{ pb: 1 }}>
                            <Box display='flex' justifyContent='space-between' alignItems='center'>
                                <Typography variant='h6'>Review {approval?.entityType} for approval</Typography>
                                <IconButton onClick={() => setOpen(false)} size='small'>
                                    <Close />
                                </IconButton>
                            </Box>
                        </DialogTitle>
                        <DialogContent sx={{ pb: 2 }}>
                            <Stack direction='row' spacing={4} mt={2}>
                                <Stack spacing={2} mt={2} flex={1}>
                                    {ApprovalManager.statusField(approval.status)}
                                    {ApprovalManager.infoField('Flow Name', approval.entityName)}
                                    {ApprovalManager.infoField('Flow Version', settlement.versioning.version)}
                                </Stack>
                                <Stack spacing={2} mt={2} flex={1}>
                                    {ApprovalManager.infoField('Rejected By', lastChange.userName ?? 'N/A')}
                                    {ApprovalManager.infoField('Rejected At', Formatter.date(new Date(lastChange.changedBy), 'mm/dd/yyyy hh:mm:ss'))}
                                </Stack>
                            </Stack>
                        </DialogContent>

                        {lastChange.description && (
                            <DialogContent sx={{ pb: 2 }}>
                                <Box>
                                    <Typography variant='subtitle2' color='text.secondary'>
                                        Rejection Reason
                                    </Typography>
                                    <Box
                                        sx={{
                                            mt: 1.5,
                                            p: 2,
                                            border: theme.palette.divider,
                                            borderRadius: '4px',
                                            backgroundColor: 'dimgray',
                                            minHeight: '200px',
                                            maxHeight: '250px',
                                            overflow: 'auto'
                                        }}>
                                        <Typography
                                            variant='body1'
                                            component='pre'
                                            sx={{
                                                whiteSpace: 'pre-wrap',
                                                wordWrap: 'break-word',
                                                margin: 0,
                                                fontFamily: 'inherit',
                                                fontSize: '1rem',
                                                lineHeight: 1.4375
                                            }}>
                                            {lastChange.description}
                                        </Typography>
                                    </Box>
                                </Box>
                            </DialogContent>
                        )}

                        <DialogContent sx={{ pb: 2 }}>
                            <Stack spacing={2} mt={3}>
                                <Typography variant='subtitle2' color='text.secondary'>
                                    Select Approval Group:
                                </Typography>
                                <Select
                                    fullWidth
                                    required
                                    size='small'
                                    value={selectedGroupUUID ?? ''}
                                    onChange={e => setSelectedGroupUUID(e.target.value)}>
                                    {groups?.map((group: IUserRole) => (
                                        <MenuItem key={group?.uuid} value={group?.uuid}>
                                            {group?.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Stack>
                            <Stack spacing={2} mt={3}>
                                <Typography variant='body1' gutterBottom>
                                    Do you want to resend to review again?
                                </Typography>
                            </Stack>
                        </DialogContent>
                        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                            <LoadingButton
                                startIcon={<SendRounded />}
                                variant='contained'
                                loading={statusExecuteSendApprovalToReview === 'loading'}
                                onClick={() => executeSendApprovalToReview(null)}
                                loadingPosition='start'
                                sx={{ textTransform: 'none' }}
                                disabled={false}>
                                Send For Review
                            </LoadingButton>
                        </DialogActions>
                    </>
                )

            default:
                return (
                    <>
                        <DialogTitle sx={{ pb: 1 }}>
                            <Box display='flex' justifyContent='space-between' alignItems='center'>
                                <Typography variant='h6' textTransform='capitalize'>{approval?.entityType} details</Typography>
                                <IconButton onClick={() => setOpen(false)} size='small'>
                                    <Close />
                                </IconButton>
                            </Box>
                        </DialogTitle>
                        <DialogContent sx={{ pb: 2 }}>
                            <Grid container spacing={2} mt={3}>
                                <Grid item xs={6}>
                                    <Stack spacing={2}>
                                        {ApprovalManager.statusField(approval.status)}
                                        {ApprovalManager.infoField('Flow Name', approval.entityName)}
                                    </Stack>
                                </Grid>
                                <Grid item xs={6}>
                                    <Stack spacing={2}>
                                        {ApprovalManager.infoField('Approved By', lastChange.userName ?? 'N/A')}
                                        {ApprovalManager.infoField('Approved At', Formatter.date(new Date(lastChange.changedAt), 'mm/dd/yyyy hh:mm:ss'))}
                                    </Stack>
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogContent sx={{ pb: 2 }}>
                            <Box mb={3}>
                                <Typography variant='body1' gutterBottom>
                                    The current approved version will remain live until the new version is approved and published.
                                </Typography>
                            </Box>
                        </DialogContent>
                        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                        </DialogActions>
                    </>
                )
        }
    }

    return (
        <Dialog open={open} onClose={() => setOpen(false)} maxWidth='sm' fullWidth PaperProps={{ sx: { borderRadius: 2, minHeight: 300 } }}>
            {renderModalContent()}
        </Dialog>
    )
}

export default memo(SettlementSubmitModal)
